import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { publicApi, clusterApi, ApiError } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import "./VerifyEmailPage.css";

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { isAuthenticated, loginWithToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);

  const navigateToDashboard = useCallback(async () => {
    try {
      const clients = await clusterApi.getClients();
      if (clients && clients.length > 0) {
        navigate(`/${clients[0].id}`);
        return;
      }
    } catch {
      // fall through
    }
    navigate("/");
  }, [navigate]);

  useEffect(() => {
    if (!token) {
      setError("Missing verification token");
      setLoading(false);
      return;
    }

    const verify = async () => {
      try {
        const response = await publicApi.verifyEmail(token);
        if (response.access_token) {
          loginWithToken(response.access_token);
        }
        setSuccess(true);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Verification failed");
      } finally {
        setLoading(false);
      }
    };

    verify();
  }, [token, loginWithToken]);

  useEffect(() => {
    if (!success || !isAuthenticated) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigateToDashboard();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [success, isAuthenticated, navigateToDashboard]);

  return (
    <div className="verify-email-page">
      <div className="verify-email-background">
        <div className="verify-email-orb orb-1"></div>
        <div className="verify-email-orb orb-2"></div>
        <div className="verify-email-orb orb-3"></div>
      </div>

      <div className="verify-email-container">
        <div className="verify-email-card">
          <div className="verify-email-header">
            <div className="verify-email-logo">
              <img src="/clusterview-logo.svg" alt="ClusterView" className="verify-email-logo-icon" style={{ width: 32, height: 32 }} />
            </div>
            <h1>ClusterView</h1>
          </div>

          {loading && (
            <div className="verify-email-content">
              <span className="verify-email-spinner"></span>
              <p className="verify-email-message">Verifying...</p>
            </div>
          )}

          {success && (
            <div className="verify-email-content">
              <CheckCircleIcon className="verify-email-success-icon" sx={{ fontSize: 48 }} />
              <p className="verify-email-message verify-email-success-text">Email Verified!</p>
              {isAuthenticated ? (
                <p className="verify-email-message" style={{ fontSize: 14, color: "#64748b" }}>
                  Redirecting to dashboard in {countdown}s...
                </p>
              ) : (
                <Link to="/login" className="verify-email-link">
                  Go to Login
                </Link>
              )}
            </div>
          )}

          {error && (
            <div className="verify-email-content">
              <ErrorIcon className="verify-email-error-icon" sx={{ fontSize: 48 }} />
              <p className="verify-email-message verify-email-error-text">{error}</p>
              <Link to="/login" className="verify-email-link">
                Go to Login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
